import type { CharacterHealthChangeEvent, CharacterLifecycleAction, CharacterLifecycleScript } from './index';

export interface BasicLifecycleScriptOptions {
  readonly id?: string;
  readonly hitAnimation?: string;
  readonly minDamageForHit?: number;
  readonly deathAnimation?: string;
  readonly despawnMode?: 'immediate' | 'after-animation';
  readonly despawnDelaySeconds?: number;
}

export const createBasicLifecycleScript = (options: BasicLifecycleScriptOptions = {}): CharacterLifecycleScript => {
  const {
    id = 'basic-lifecycle',
    hitAnimation,
    minDamageForHit = 0,
    deathAnimation,
    despawnMode = 'after-animation',
    despawnDelaySeconds
  } = options;

  const script: CharacterLifecycleScript = {
    id,
    onHealthChanged: (event: CharacterHealthChangeEvent): CharacterLifecycleAction[] | void => {
      const actions: CharacterLifecycleAction[] = [];

      if (event.cause !== 'damage') {
        return undefined;
      }

      const delta = event.delta;
      const tookDamage = delta < 0;

      if (!tookDamage) {
        return undefined;
      }

      const damageAmount = Math.abs(delta);
      const wasAlive = event.previousHealth > 0;
      const isDead = event.currentHealth <= 0;

      if (isDead && wasAlive) {
        if (deathAnimation) {
          actions.push({ type: 'play-death', animation: deathAnimation });
        }
        const despawnModeForEvent = deathAnimation || despawnMode !== 'after-animation' ? despawnMode : 'immediate';
        actions.push({ type: 'despawn', mode: despawnModeForEvent, delaySeconds: despawnDelaySeconds });
        return actions.length > 0 ? actions : undefined;
      }

      if (hitAnimation && event.currentHealth > 0 && damageAmount >= minDamageForHit) {
        actions.push({ type: 'play-hit', animation: hitAnimation });
      }

      return actions.length > 0 ? actions : undefined;
    }
  };

  return script;
};
